import { useState } from "react";
import { Link } from "react-router-dom";
import { OPERATOR_NAME } from "../../../shared/legal";
import { api } from "../../lib/api";
import { ArrowIcon, CheckIcon } from "../../lib/ui";
import { LegalPage } from "./LegalPage";
import { Label, PrivacyMail } from "./parts";

type Who = "org" | "payer";
type Step = "form" | "code" | "done";

/** Words for a rate-limited or failed call, taken from the server when it sent them. */
function problem(r: { error: string; retryAfter?: number }): string {
  if (r.retryAfter) {
    const mins = Math.max(1, Math.ceil(r.retryAfter / 60));
    return `${r.error} Try again in about ${mins} ${mins === 1 ? "minute" : "minutes"}.`;
  }
  return r.error || "Something went wrong. Please try again.";
}

const Tick = ({ color }: { color: string }) => (
  <span style={{ color, display: "inline-flex", flex: "none" }}>
    <CheckIcon size={22} />
  </span>
);

const InfoIcon = () => (
  <svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="var(--amber-ink)" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" style={{ flex: "none" }}>
    <circle cx="12" cy="12" r="9" />
    <path d="M12 11v5M12 8h.01" />
  </svg>
);

export default function DeleteData() {
  const [who, setWho] = useState<Who>("org");
  const [step, setStep] = useState<Step>("form");
  const [link, setLink] = useState("");
  const [reference, setReference] = useState("");
  const [email, setEmail] = useState("");
  const [scope, setScope] = useState("");
  const [understood, setUnderstood] = useState(false);
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [devCode, setDevCode] = useState("");

  function pick(next: Who) {
    if (next === who) return;
    setWho(next);
    setScope("");
    setStep("form");
    setError("");
    setCode("");
  }

  const fields = () => ({
    kind: who,
    scope,
    email: email.trim(),
    ...(who === "org" ? { link: link.trim() } : { reference: reference.trim() }),
  });

  async function send(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError("");
    const r = await api.post<{ ok: true; devCode?: string }>("/api/data-request/start", fields());
    setBusy(false);
    if (!r.ok) {
      setError(problem(r));
      return;
    }
    setDevCode(r.data.devCode ?? "");
    setStep("code");
  }

  async function confirm(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError("");
    const r = await api.post<{ ok: true }>("/api/data-request/confirm", { ...fields(), code });
    setBusy(false);
    if (!r.ok) {
      setError(problem(r));
      return;
    }
    setStep("done");
  }

  function backToForm() {
    setStep("form");
    setError("");
    setCode("");
  }

  function another() {
    backToForm();
    setScope("");
    setUnderstood(false);
  }

  const stage = step === "form" ? 1 : step === "code" ? 2 : 3;
  const stepCls = (n: number) => `lg-step ${n < stage ? "lg-step--done" : n === stage ? "lg-step--on" : "lg-step--off"}`;

  return (
    <LegalPage tab="delete" title="Delete my data">
      <section className="wrap" style={{ paddingTop: 48, paddingBottom: 40 }}>
        <div className="lg-eyebrow">Your data · Delete on request</div>
        <h1 className="display" style={{ marginTop: 18, maxWidth: 980, fontSize: "min(76px, 11vw)" }}>
          Ask us to delete your data.
        </h1>
        <p className="lead" style={{ marginTop: 26, maxWidth: 700 }}>
          Tell us who you are and what to delete, and we’ll check it’s you. It’s free, and it’s done within 30 days. Here is exactly what happens, including what we may have to keep.
        </p>
        <div style={{ display: "flex", flexWrap: "wrap", gap: 10, marginTop: 30 }}>
          <span className="lg-pill">Free</span>
          <span className="lg-pill">Done within 30 days</span>
          <span className="lg-pill">Checked with an emailed code</span>
        </div>
      </section>

      <section className="wrap" style={{ paddingBottom: 56 }}>
        <div className="lg-talk">
          <b>STRAIGHT TALK ABOUT SINGAPORE LAW</b>
          <p>
            The PDPA has no blanket “right to be forgotten”. We honour deletion requests anyway, by treating them as a withdrawal of consent (section 16) and because we must not keep personal data longer than we need it (section 25). What we may still have to keep is listed below, in full.
          </p>
        </div>
      </section>

      <section className="wrap" style={{ paddingBottom: 88 }}>
        <div className="lg-main">
          <div className="lg-card">
            <h2 className="h1" style={{ fontSize: 30 }}>Make a request</h2>
            <p className="lg-sub">Choose the one that fits you.</p>
            <div className="lg-paths">
              <button type="button" className="lg-path" aria-pressed={who === "org"} onClick={() => pick("org")}>
                <b>I run an organisation</b>
                <span>Close the account and delete its data, or clear payer names and emails.</span>
              </button>
              <button type="button" className="lg-path" aria-pressed={who === "payer"} onClick={() => pick("payer")}>
                <b>I paid an organisation</b>
                <span>Remove my name and email from a payment request.</span>
              </button>
            </div>

            {step === "form" && (
              <form onSubmit={send}>
                {who === "org" ? (
                  <>
                    <div className="lg-fld">
                      <label className="lbl" htmlFor="dd-link">Your organisation’s link</label>
                      <input id="dd-link" className="inp" type="text" value={link} onChange={(e) => setLink(e.target.value)} placeholder="confirmpay.link/your-link" autoComplete="off" required />
                      <p className="lg-hint">It’s in your dashboard, under Your public link.</p>
                    </div>
                    <div className="lg-fld">
                      <label className="lbl" htmlFor="dd-email">Contact email on the account</label>
                      <input id="dd-email" className="inp" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@yourorganisation.org" autoComplete="email" required />
                      <p className="lg-hint">We email a 6-digit code here to check it’s you.</p>
                    </div>
                    <fieldset className="lg-fieldset">
                      <legend>What should we delete?</legend>
                      <label className="lg-opt">
                        <input type="radio" name="scope" value="all" checked={scope === "all"} onChange={() => setScope("all")} required />
                        <span><b>Everything.</b> Close my organisation and delete all its data.<small>Your payment links and QR codes stop working for good.</small></span>
                      </label>
                      <label className="lg-opt">
                        <input type="radio" name="scope" value="payers" checked={scope === "payers"} onChange={() => setScope("payers")} required />
                        <span><b>Only payer names and emails.</b> Keep my account.<small>Requests stay, with the payer details cleared.</small></span>
                      </label>
                    </fieldset>
                    <p className="lg-note">Want your records first? Export them from your dashboard (Export CSV) before you send this.</p>
                  </>
                ) : (
                  <>
                    <div className="lg-fld">
                      <label className="lbl" htmlFor="dd-ref">Request reference</label>
                      <input id="dd-ref" className="inp inp--mono" type="text" value={reference} onChange={(e) => setReference(e.target.value)} placeholder="MBH-014" autoComplete="off" required />
                      <p className="lg-hint">It’s on your receipt and on the payment page.</p>
                    </div>
                    <div className="lg-fld">
                      <label className="lbl" htmlFor="dd-pemail">The email you entered</label>
                      <input id="dd-pemail" className="inp" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@example.com" autoComplete="email" required />
                      <p className="lg-hint">
                        We email a 6-digit code here. Didn’t give an email? Write to <PrivacyMail /> instead and we’ll check with the organisation.
                      </p>
                    </div>
                    <fieldset className="lg-fieldset">
                      <legend>How much should we remove?</legend>
                      <label className="lg-opt">
                        <input type="radio" name="scope" value="one" checked={scope === "one"} onChange={() => setScope("one")} required />
                        <span><b>Just this request.</b><small>Your name and email come off it. The amount, reference and date stay.</small></span>
                      </label>
                      <label className="lg-opt">
                        <input type="radio" name="scope" value="email" checked={scope === "email"} onChange={() => setScope("email")} required />
                        <span><b>Every request that used this email.</b><small>Across all organisations that used ConfirmPay.</small></span>
                      </label>
                    </fieldset>
                  </>
                )}

                <label className="lg-opt" style={{ marginTop: 24 }}>
                  <input type="checkbox" checked={understood} onChange={(e) => setUnderstood(e.target.checked)} required />
                  <span>I understand this can’t be undone.</span>
                </label>
                {error && <div className="error-box" role="alert">{error}</div>}
                <div className="lg-actions">
                  <span className="lg-hint" style={{ margin: 0, maxWidth: 300 }}>Free. We only use these details to handle this request.</span>
                  <button className="btn" type="submit" disabled={busy}>
                    {busy ? "Sending…" : "Send request"}
                    <ArrowIcon size={20} />
                  </button>
                </div>
              </form>
            )}

            {step === "code" && (
              <form onSubmit={confirm} style={{ marginTop: 36 }}>
                <h3 style={{ margin: 0, fontSize: 26, fontWeight: 800, letterSpacing: "-0.03em" }}>Check your email.</h3>
                <p className="lg-sub">
                  If the details match, we’ve sent a 6-digit code to {email.trim() || "the address you gave"}. It works for 10 minutes. Enter it to confirm the request.
                </p>
                {devCode && <p className="lg-note">Local dev only: your code is {devCode}.</p>}
                <div className="lg-fld">
                  <label className="lbl" htmlFor="dd-code">6-digit code</label>
                  <input
                    id="dd-code"
                    className="inp codein"
                    type="text"
                    inputMode="numeric"
                    pattern="[0-9]*"
                    autoComplete="one-time-code"
                    maxLength={6}
                    autoFocus
                    value={code}
                    onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
                    placeholder="000000"
                  />
                  <p className="lg-hint">Nothing is logged until the code is entered. No email after a few minutes? Check spam, then check the details match your account or receipt.</p>
                </div>
                {error && <div className="error-box" role="alert">{error}</div>}
                <div className="lg-actions">
                  <button type="button" className="textbtn" style={{ fontSize: 15 }} onClick={backToForm}>Change something</button>
                  <button className="btn" type="submit" disabled={busy || code.length !== 6}>
                    {busy ? "Checking…" : "Confirm request"}
                  </button>
                </div>
              </form>
            )}

            {step === "done" && (
              <div style={{ marginTop: 36 }} role="status">
                <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                  <span style={{ display: "grid", placeItems: "center", width: 36, height: 36, borderRadius: "50%", background: "var(--sage)", color: "#0e1007" }}>
                    <CheckIcon size={20} strokeWidth={2.6} />
                  </span>
                  <h3 style={{ margin: 0, fontSize: 26, fontWeight: 800, letterSpacing: "-0.03em" }}>Request logged.</h3>
                </div>
                <p className="lg-sub">
                  We’ll email you when it’s finished, within 30 days and usually much sooner. The email lists what we deleted and anything we had to keep, with the reason.
                </p>
                <div className="lg-actions">
                  <button type="button" className="textbtn" style={{ fontSize: 15 }} onClick={another}>Make another request</button>
                  <Link className="btn" to="/privacy">Back to the Privacy Policy</Link>
                </div>
              </div>
            )}
          </div>

          <aside className="lg-rail" aria-label="What happens next">
            <h2>What happens next</h2>
            <ol>
              <li className={stepCls(1)}><span className="lg-dot">1</span><div><div className="lg-step-t">You ask</div><div className="lg-step-d">Tell us who you are and what to delete.</div></div></li>
              <li className={stepCls(2)}><span className="lg-dot">2</span><div><div className="lg-step-t">We check it’s you</div><div className="lg-step-d">We email a 6-digit code to the address on file. It works for 10 minutes.</div></div></li>
              <li className={stepCls(3)}><span className="lg-dot">3</span><div><div className="lg-step-t">We delete</div><div className="lg-step-d">Within 30 days, and usually much sooner.</div></div></li>
              <li className={stepCls(4)}><span className="lg-dot">4</span><div><div className="lg-step-t">We confirm</div><div className="lg-step-d">A short email lists what was deleted and anything we had to keep.</div></div></li>
            </ol>
          </aside>
        </div>
      </section>

      <section className="wrap rule-top" style={{ paddingTop: 80, paddingBottom: 88 }}>
        <h2 className="h2" style={{ maxWidth: 760, fontSize: "min(48px, 9vw)" }}>What goes, and what may stay.</h2>
        <div className="lg-two" style={{ marginTop: 44 }}>
          <div>
            <h3 style={{ margin: "0 0 18px", fontSize: 24, fontWeight: 700, letterSpacing: "-0.02em" }}>What we delete</h3>
            <div className="lg-li"><Tick color="var(--sage-ink)" /><span><b>Your organisation’s details:</b> name, contact email, PayNow number or UEN.</span></div>
            <div className="lg-li"><Tick color="var(--sage-ink)" /><span><b>Your forwarding address</b> and sign-in sessions.</span></div>
            <div className="lg-li"><Tick color="var(--sage-ink)" /><span><b>Every payment request</b> and the payer names and emails on it.</span></div>
            <div className="lg-li"><Tick color="var(--sage-ink)" /><span><b>The records of forwarded bank emails</b> we kept to match payments.</span></div>
            <div className="lg-li"><Tick color="var(--sage-ink)" /><span><b>For payers:</b> your name and email on the request.</span></div>
          </div>
          <div>
            <h3 style={{ margin: "0 0 18px", fontSize: 24, fontWeight: 700, letterSpacing: "-0.02em" }}>What may stay, and why</h3>
            <div className="lg-li"><InfoIcon /><span><b>The amount, reference and date of a payment,</b> with no name or email. It’s the organisation’s record of money received.</span></div>
            <div className="lg-li"><InfoIcon /><span><b>Your link name,</b> reserved with no personal data attached, so old posters can’t be claimed by someone else.</span></div>
            <div className="lg-li"><InfoIcon /><span><b>Anything the law requires us to keep,</b> or that we need to handle a legal claim.</span></div>
            <div className="lg-li"><InfoIcon /><span><b>Routine backups,</b> until they age out. We don’t restore them except to recover from a failure.</span></div>
            <div className="lg-li"><InfoIcon /><span><b>Copies the organisation already has,</b> such as receipt emails or exported files. We can’t recall those; ask the organisation.</span></div>
          </div>
        </div>
      </section>

      <section className="wrap rule-top" style={{ paddingTop: 80, paddingBottom: 88 }}>
        <h2 className="h2" style={{ maxWidth: 760, fontSize: "min(48px, 9vw)" }}>Before you go.</h2>
        <div className="lg-three" style={{ marginTop: 44 }}>
          <div><h3>Want a copy first?</h3><p>Organisations can export every request as a CSV from the dashboard. Anyone can also ask for access to their data under the PDPA. We reply within 30 days, free of charge.</p></div>
          <div><h3>Just want to stop forwarding?</h3><p>Delete the filter in your own inbox and forwarding stops at once. You can also rotate your private forwarding address from the dashboard.</p></div>
          <div><h3>Something wrong, not unwanted?</h3><p>Ask for a correction instead and we’ll fix it as soon as practicable. <Link to="/privacy#c8">See your rights</Link>.</p></div>
        </div>
      </section>

      <section className="wrap lg-faq" style={{ paddingBottom: 88 }}>
        <h2 className="h2" style={{ maxWidth: 760, marginBottom: 36, fontSize: "min(48px, 9vw)" }}>Questions people ask.</h2>
        <details>
          <summary>Will payments still go through after I close my organisation?</summary>
          <p>Payments to your PayNow number still happen, because they go from the payer’s bank to yours. ConfirmPay just stops matching them, marking requests paid and emailing receipts.</p>
        </details>
        <details>
          <summary>Can I undo a deletion?</summary>
          <p>Not once it’s done. Until we finish, write to us and we’ll stop.</p>
        </details>
        <details>
          <summary>When might you say no?</summary>
          <p>Only if we can’t confirm the request comes from the person it’s about, if the law requires us to keep something, or if the data belongs to someone else’s request. We’ll tell you why in writing.</p>
        </details>
        <details>
          <summary>I’m not happy with the answer.</summary>
          <p>Talk to our Data Protection Officer first. If that doesn’t settle it, you can complain to the Personal Data Protection Commission at pdpc.gov.sg.</p>
        </details>
      </section>

      <section className="wrap" style={{ paddingBottom: 88 }}>
        <div className="lg-eyebrow" style={{ marginBottom: 14 }}>Prefer to write?</div>
        <div className="lg-contact" style={{ marginTop: 0 }}>
          <div><Label>Data Protection Officer</Label>{OPERATOR_NAME}</div>
          <div><Label>Email</Label><PrivacyMail /></div>
          <div><Label>Regulator</Label>Personal Data Protection Commission · pdpc.gov.sg</div>
        </div>
      </section>
    </LegalPage>
  );
}
